import BookmarkButton from "@/bookmarks/BookmarkButton"
import DeleteBrainSearchButton from "@/brain-search/internal/DeleteBrainSearchButton"
import { BrainSearchMoreActionsButtonFragment$key } from "@/brain-search/internal/__generated__/BrainSearchMoreActionsButtonFragment.graphql"
import { useToggleBrainSearchFullView } from "@/brain-search/internal/drawer/BrainSearchSidePanelToggle"
import { useBrainSearchWidth } from "@/brain-search/internal/utils/BrainSearchUtils"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoDivider } from "@disco-ui"
import DiscoDropdownItem from "@disco-ui/dropdown/DiscoDropdownItem"
import DiscoMoreActionsDropdown from "@disco-ui/dropdown/DiscoMoreActionsDropdown"
import usePermissions from "@utils/hook/usePermissions"
import classNames from "classnames"
import { graphql, useFragment } from "react-relay"

interface Props {
  brainSearchKey?: BrainSearchMoreActionsButtonFragment$key | null
  inHeader?: boolean
}

export default function BrainSearchMoreActionsButton(props: Props) {
  const { brainSearchKey, inHeader = false } = props
  const classes = useStyles()
  const drawer = useGlobalDrawer("brainSearch")
  const { isWide, isMobile } = useBrainSearchWidth()

  const brainSearch = useFragment<BrainSearchMoreActionsButtonFragment$key>(
    graphql`
      fragment BrainSearchMoreActionsButtonFragment on BrainSearch {
        id
        hasViewerBookmarked
        contentUsage {
          id
        }
        ...usePermissionsFragment
        ...DeleteBrainSearchButtonFragment
        ...BookmarkButtonBrainSearchFragment
      }
    `,
    brainSearchKey || null
  )
  const toggleFullView = useToggleBrainSearchFullView({
    contentUsageId: brainSearch?.contentUsage?.id,
  })

  const canManageBrainSearch = usePermissions(brainSearch).has("brain_search.manage")
  if (brainSearch && !canManageBrainSearch) return null

  return (
    <DiscoMoreActionsDropdown
      moreActionsButtonClassName={classNames(
        classes.moreActionsButton,
        inHeader && classes.headerButton
      )}
      testid={`more-actions`}
      rotateIcon
    >
      {renderItems()}
    </DiscoMoreActionsDropdown>
  )

  function renderItems() {
    const deleteButton = brainSearch && (
      <DeleteBrainSearchButton brainSearchKey={brainSearch} onDelete={onDelete}>
        {(buttonProps) => (
          <DiscoDropdownItem {...buttonProps} icon={"trash"} title={"Delete"} />
        )}
      </DeleteBrainSearchButton>
    )

    if (!inHeader || isWide) return deleteButton

    return (
      <>
        {brainSearch && (
          <>
            <DiscoDropdownItem
              icon={"edit"}
              title={"New Conversation"}
              onClick={handleClickNew}
            />
            <DiscoDivider marginTop={0.5} marginBottom={0.5} />
            <BookmarkButton
              kind={"brain_search"}
              brainSearchKey={brainSearch}
              variant={"dropdown-item"}
            />
          </>
        )}
        <DiscoDropdownItem
          icon={"time"}
          title={"View History"}
          onClick={handleClickHistory}
        />
        {deleteButton && <DiscoDivider marginTop={0.5} marginBottom={0.5} />}
        {deleteButton}
      </>
    )
  }

  function onDelete() {
    // Redirect to empty state if we're deleting the brain search we're currently viewing
    if (!brainSearch) return
    if (drawer.params.convId === brainSearch.id) {
      drawer.setParams({ convId: undefined })
    }
  }

  function handleClickNew() {
    drawer.setParams({
      convId: undefined,
      sourceId: brainSearch?.contentUsage?.id,
    })
  }

  function handleClickHistory() {
    // On mobile, navigate to the history view
    if (isMobile) {
      drawer.setParams({ drawerView: "history", convId: undefined })
      return
    }
    // When in non-mobile side panel, go to full view to see history in sidebar
    toggleFullView()
  }
}

const useStyles = makeUseStyles((theme) => ({
  moreActionsButton: {
    "&.Mui-selected": {
      "& svg": {
        color: theme.palette.common.black,
      },
      "& $rightContent": {
        "& svg": {
          color: theme.palette.common.black,
          "&:hover": {
            color: theme.palette.common.black,
          },
        },
      },
    },
    "&.Mui-selected:hover": {
      "& svg": {
        color: theme.palette.common.black,
      },
    },
    "&:hover": {
      background: "rgba(0,0,0,.1)",
    },
  },
  headerButton: {
    width: 40,
    height: 40,
  },
}))
